import React, { useState, useEffect } from 'react';
// Common Components (Corrected Paths)
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API & Utils (Corrected Paths)
import { getGrns, fetchGrn, destroyGrn /*, destroyGrnsMultiple */ } from '../../../api/GoodsReceivedNotesAPI'; // Import GRN API functions
import { handleApiError } from '../../../api/risapi';
// Report component if needed
// import GrnReport from './GrnReport';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns'; // For formatting date

const GrnPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Goods Received Note'); // Updated entity name
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    // Multi-select state (Commented out - Deletion complex)
    // const [enableMultipleItemSelect, setEnableMultipleItemSelect] = useState(false);
    // const [selectedGrnIds, setSelectedGrnIds] = useState(new Set());

    const [grnsList, setGrnsList] = useState({ // Renamed state
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false); // Triggers refresh

    // --- Navigation ---
    // GRNs are often created *from* another document, but a manual create might be needed
    const handleAddClick = () => navigate('/franchisee_user/inventory/grn/create'); // Manual create route
    const handleEditClick = (id) => navigate(`/franchisee_user/inventory/grn/edit/${id}`); // Edit route
    // Optional: View details
    // const handleViewClick = (id) => navigate(`/franchisee_user/inventory/grn/view/${id}`);

    // --- Delete Logic ---
    const handleDeleteClick = (id) => {
        fetchGrn(id, function (result) { // Use fetchGrn
            if (result.grn) {
                const deleteItem = result.grn;
                const sourceDisplay = deleteItem.sourceable ? `${deleteItem.sourceable_type.split('\\').pop()} #${deleteItem.sourceable_id}` : 'Manual';

                Swal.fire({
                    title: 'Are you sure?',
                    html: `
                        <p>Delete GRN:</p>
                        <ul class="delete-details-list">
                            <li><strong>GRN No:</strong> ${deleteItem.grn_number || `#${deleteItem.id}`}</li>
                            <li><strong>Date:</strong> ${deleteItem.grn_date}</li>
                            <li><strong>Source:</strong> ${sourceDisplay}</li>
                        </ul>
                        <p class="text-danger">WARNING: Deleting a GRN can corrupt stock history if items have been used. Proceed with caution!</p>
                        <p>This action cannot be undone.</p>`,
                    icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#6c757d', confirmButtonText: 'Yes, delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyGrn(deleteItem, function (apiResponse) { // Use destroyGrn
                            if (apiResponse.grn) {
                                Swal.fire('Deleted!', 'GRN record deleted.', 'success');
                                setSaveCompleted(prev => !prev); // Trigger refresh
                            } else {
                                // Display specific backend error (like conflict due to stock usage)
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete GRN.', 'error');
                            }
                        });
                    }
                });
            } else { Swal.fire('Error!', 'Could not fetch GRN details.', 'error'); }
        });
    };

    // --- Multi-Delete Logic (Commented Out - Complex due to stock checks) ---
    /*
     const showMultipleDeleteConfirmation = () => {
         Swal.fire('Not Recommended', 'Deleting multiple GRNs is risky for stock accuracy.', 'warning');
         // Complex logic needed:
         // 1. Fetch details for all selected GRNs.
         // 2. Check backend if *any* stock from *any* selected GRN item has been used.
         // 3. If used, block deletion.
         // 4. If safe, proceed with destroyGrnsMultiple API call.
     };
    */
    // --- Checkbox Handler (Commented Out) ---
    // const handleCheckboxChange = (id, isChecked) => { ... };

    // --- Effects ---
    useEffect(() => { /* ... success message effect ... */ }, [successMessage, location.pathname, navigate]);
    useEffect(() => {
         document.title = 'Manage Goods Received Notes';
         let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
         setViewDataLoading(true);
         // setSelectedGrnIds(new Set());
         // setEnableMultipleItemSelect(false);

         getGrns(filters, function (response) { // Use getGrns
             setViewDataLoading(false);
             if (response.grns) { // Check for 'grns' key
                 let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                 if (Array.isArray(response.grns)) { data = response.grns; }
                 else if (response.grns.data && Array.isArray(response.grns.data)) {
                     data = response.grns.data; currentP = response.grns.current_page; lastP = response.grns.last_page; responsePP = response.grns.per_page;
                 } else { console.error("Unexpected API structure:", response.grns); handleApiError(response); return; }
                 setGrnsList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP }); // Update state variable name
             } else {
                  handleApiError(response);
                  setToaster({ type: 'danger', heading: 'Error', message: 'Could not load GRNs.' });
                  setShowToaster(true);
                  setGrnsList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
             }
         });
     }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try { return format(parseISO(dateString), 'dd-MMM-yyyy'); }
        catch (error) { return dateString; }
    };

    // Helper to display source info
    const getSourceDisplay = (grn) => {
        if (grn.sourceable) {
            const type = grn.sourceable_type.split('\\').pop(); // Get class name
            let number = grn.sourceable.invoice_number || grn.sourceable.challan_number || `#${grn.sourceable_id}`; // Get relevant number
             if (type === 'PurchaseInvoice') return `PI: ${number || grn.sourceable_id}`;
             if (type === 'StockTransferChallan') return `Transfer: ${number || grn.sourceable_id}`;
             if (type === 'OpeningBalance') return `Opening Bal: #${grn.sourceable_id}`; // Example
             return `${type}: #${grn.sourceable_id}`;
        }
        return 'Manual Entry';
    }

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='GRN List'>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Goods Received Notes</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All GRNs</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            {/* Optional: Add button for manual GRN */}
                                            {/* <button className="text-nowrap btn btn-primary btn-sm m-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}> <i className="ti ti-plus f-s-18"></i> Add Manual GRN </button> */}
                                            {/* Add GrnReport component here */}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {grnsList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={grnsList.current_page} totalPages={grnsList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            <div className='d-flex align-items-center ms-auto mt-2 mt-md-0'>
                                                 {/* Multi-select button placeholder (Commented Out) */}
                                                 {/* <button ...>Select</button> */}
                                                 {/* <button ...>Delete Selected</button> */}
                                            </div>
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No Goods Received Notes found.</p> )}
                                    {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                {/* --- Card Layout Start --- */}
                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && grnsList.data.map((grn) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={grn.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            {/* Checkbox (if multi-select) */}
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-truck-delivery f-s-18"></i> {/* GRN Icon */}
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{grn.grn_number || `#${grn.id}`}</h6>
                                                                <small className="text-muted d-block">{formatDate(grn.grn_date)}</small>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                     {/* Optional: View Action */}
                                                                    {/* <li><a className="dropdown-item" onClick={() => handleViewClick(grn.id)}><i className="ti ti-eye text-info me-1" /> View</a></li> */}
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(grn.id)}><i className="ti ti-edit text-success me-1" /> Edit</a></li>
                                                                    <li><button className="dropdown-item delete-button" onClick={() => handleDeleteClick(grn.id)}><i className="ti ti-trash text-danger me-1" /> Delete</button></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body py-2">
                                                         {/* Display Source Info */}
                                                         <div className="d-flex justify-content-between align-items-center">
                                                              <div>
                                                                   <p className="f-w-500 text-secondary mb-0">Source</p>
                                                                   <h6 className="f-w-600 mb-0">{getSourceDisplay(grn)}</h6>
                                                              </div>
                                                              {/* Optional: Display total items or value */}
                                                               {/* <div className='text-end'>
                                                                   <p className="f-w-500 text-secondary mb-0">Items</p>
                                                                   <h6 className="f-w-600 mb-0">{grn.items_count || 'N/A'}</h6> // Requires withCount('items') in backend
                                                              </div> */}
                                                         </div>
                                                     </div>
                                                </div>
                                            </div>
                                        ))}
                                        {/* No Data Card */}
                                        {!viewDataLoading && grnsList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No GRNs Found</h5><p className="card-text">No records match your criteria or no GRNs created yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>
                                {/* --- Card Layout End --- */}

                               
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default GrnPage;